<template>
  <div class="enquete-wrapper" :key="item.id">
    <!-- SVG background: red bar + corner decorations -->
    <Background class="enquete-bg" />

    <!-- Content overlay — sits on top of the SVG -->
    <div class="enquete-overlay">
      <!-- Question strip -->
      <div class="enquete-question" v-if="!hideTitle">
        <span class="question-text" :style="questionTextStyles">
          {{ item.dsc_titulo_enquete }}
        </span>
      </div>

      <!-- Options row -->
      <div class="enquete-options" :style="optionsRowStyles">
        <transition-group
          name="option-slide"
          tag="div"
          class="options-inner"
          :style="optionsInnerStyles"
        >
          <div
            v-for="(opcao, index) in item.enquete_opcoes"
            :key="opcao.id"
            class="enquete-option"
            :style="optionWrapperStyles(index)"
          >
            <!-- Letter badge / image -->
            <div class="option-letter mb-1" :style="letterBadgeStyles">
              <img
                v-if="opcao.url_imagem"
                :src="opcao.url_imagem"
                :alt="letters[index]"
                style="width: 100%; height: 100%; object-fit: cover; display: block"
              />
              <span v-else :style="letterTextStyles">{{ letters[index] }}</span>
            </div>

            <!-- Data block: label + percentage bar -->
            <div class="option-data">
              <div class="option-label-bar mb-1" :style="labelBarStyles" v-if="!hideOptionTitle">
                <span class="option-label" :style="labelTextStyles">
                  {{ opcao.dsc_titulo }}
                </span>
              </div>

              <div
                v-if="item.flg_exibir_resultado"
                class="option-percent-row mb-1"
                :style="percentRowStyles"
              >
                <div class="bar-fill teal-fill" :style="tealFillStyles(index)"></div>
                <div class="bar-fill yellow-fill" :style="yellowFillStyles"></div>
                <span class="percent-text" :style="percentTextStyles">
                  {{ animatedPercentages[index] || 0 }}%
                </span>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script>
import Background from "@/assets/TVZ/Enquetes/bg-tarja-tvz-enquetes.svg";

const anim = {
  frames: {}, // { 'pct_0': rafId, ... }
  targets: {}, // { 'pct_0': targetValue, ... }
};

export default {
  name: "CardTarjaEnqueteTVZ",

  components: { Background },

  props: {
    item: {
      type: Object,
      required: true,
    },
    posicaoEnquete: String,
    hideTitle: Boolean,
    hideOptionTitle: Boolean,
    isVerticalBar: Boolean,
    isRenderedWithQRCode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      letters: ["A", "B", "C", "D", "E", "F"],

      TVZTheme: {
        colors: {
          teal: "#00A493",
          yellow: "#FEF000",
          white: "#FFFFFF",
        },
        typography: {
          titleFont: "Gobold Regular",
          labelFont: "Gobold Thin",
        },
      },

      animationDuration: 1800,

      // Reactive animated percentage values (driven by RAF via $set)
      animatedPercentages: [],

      previousResultsVisible: false,
    };
  },

  computed: {
    /* ─── Responsive scale ─── */
    screenWidth() {
      return this.$vuetify.breakpoint.width;
    },
    screenHeight() {
      return this.$vuetify.breakpoint.height;
    },

    responsiveScale() {
      const widthScale = this.screenWidth / 1920;
      const heightScale = this.screenHeight / 1080;
      return Math.max(0.5, Math.min(1.2, Math.min(widthScale, heightScale)));
    },

    s() {
      return this.responsiveScale;
    },

    optionCount() {
      return this.item?.enquete_opcoes?.length || 0;
    },
    
    roundedPercentages() {
      const raw = (this.item?.enquete_opcoes || []).map(
        (opcao) => Number(opcao.resultado) || 0
      );
      const total = raw.reduce((acc, v) => acc + v, 0);
      if (total <= 0) return raw.map(() => 0);

      const scaled = raw.map((v) => (v / total) * 100);
      const floors = scaled.map((v) => Math.floor(v));
      let remainder = 100 - floors.reduce((acc, v) => acc + v, 0);

      const byFraction = scaled
        .map((v, i) => ({ i, frac: v - Math.floor(v) }))
        .sort((a, b) => b.frac - a.frac);

      const result = floors.slice();
      for (let k = 0; k < byFraction.length && remainder > 0; k++) {
        result[byFraction[k].i] += 1;
        remainder -= 1;
      }
      return result;
    },
    barHeightPx() {
      return Math.round(151 * this.s);
    },
    questionHeightPx() {
      return Math.round(32 * this.s);
    },
    bottomPaddingPx() {
      return Math.round(5 * this.s);
    },
    badgeSizePx() {
      return Math.round(67 * this.s);
    },
    labelBarHeightPx() {
      return Math.round(25 * this.s);
    },
    percentBarHeightPx() {
      return Math.round(35 * this.s);
    },
    leftInsetPx() {
      return Math.round(56 * this.s);
    },
    rightInsetPx() {
      return Math.round(74 * this.s);
    },
    badgeMarginPx() {
      return Math.round(12 * this.s);
    },
    optionGapPx() {
      return Math.round(12 * this.s);
    },

    optionsRowHeightPx() {
      return this.barHeightPx - this.questionHeightPx - this.bottomPaddingPx;
    },

    totalGapPx() {
      return Math.max(0, this.optionCount - 1) * this.optionGapPx;
    },

    availableWidthPx() {
      return Math.round((1920 - 56 - 74) * this.s) - this.totalGapPx;
    },

    optionWidthPx() {
      return this.optionCount ? Math.floor(this.availableWidthPx / this.optionCount) : 0;
    },

    /* ─── Style objects ─── */

    questionTextStyles() {
      return {
        color: "#FFFFFF",
        fontFamily: `${this.TVZTheme.typography.titleFont} !important`,
        fontSize: `${Math.round(25 * this.s)}px`,
        fontWeight: "900",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      };
    },

    optionsRowStyles() {
      return {
        height: `${this.optionsRowHeightPx}px`,
        paddingLeft: `${this.leftInsetPx}px`,
        paddingRight: `${this.rightInsetPx}px`,
        display: "flex",
        alignItems: "flex-start",
        overflow: "hidden",
      };
    },

    optionsInnerStyles() {
      return {
        display: "flex",
        flexDirection: "row",
        width: "100%",
        height: "100%",
        alignItems: "center",
        gap: `${this.optionGapPx}px`,
      };
    },

    letterBadgeStyles() {
      return {
        width: `${this.badgeSizePx}px`,
        height: `${this.badgeSizePx}px`,
        minWidth: `${this.badgeSizePx}px`,
        backgroundColor: this.TVZTheme.colors.teal,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: "0",
        overflow: "hidden",
        marginRight: `${this.badgeMarginPx}px`,
      };
    },

    letterTextStyles() {
      return {
        color: this.TVZTheme.colors.white,
        fontFamily: `${this.TVZTheme.typography.titleFont} !important`,
        fontSize: `${Math.round(40 * this.s)}px`,
        fontWeight: "900",
        lineHeight: "1",
      };
    },

    labelBarStyles() {
      return {
        height: `${this.labelBarHeightPx}px`,
        display: "flex",
        alignItems: "center",
        // padding: `0 ${Math.round(6 * this.s)}px`,
        width: "100%",
        overflow: "hidden",
      };
    },

    labelTextStyles() {
      return {
        color: this.TVZTheme.colors.white,
        fontFamily: `${this.TVZTheme.typography.labelFont} !important`,
        fontSize: `${Math.round(20 * this.s)}px`,
        fontWeight: "700",
        textTransform: "uppercase",
        letterSpacing: "0.03em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      };
    },

    percentRowStyles() {
      return {
        display: "flex",
        flexDirection: "row",
        height: `${this.percentBarHeightPx}px`,
        position: "relative",
        width: "100%",
        overflow: "hidden",
      };
    },

    yellowFillStyles() {
      return {
        flex: "1",
        height: "100%",
        backgroundColor: this.TVZTheme.colors.yellow,
        flexShrink: "0",
      };
    },

    percentTextStyles() {
      return {
        position: "absolute",
        left: `${Math.round(6 * this.s)}px`,
        bottom: `${Math.round(6 * this.s)}px`,
        color: this.TVZTheme.colors.white,
        fontFamily: `${this.TVZTheme.typography.titleFont} !important`,
        fontSize: `${Math.round(25 * this.s)}px`,
        fontWeight: "900",
        lineHeight: "1",
        textShadow: "1px 2px 2px rgba(0,165,148,1)",
        zIndex: "1",
        pointerEvents: "none",
      };
    },
  },

  methods: {
    optionWrapperStyles(index) {
      return {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        flex: "1",
        minWidth: "0",
        transitionDelay: `${index * 60}ms`,
      };
    },

    tealFillStyles(index) {
      const pct = this.item?.flg_exibir_resultado
        ? this.animatedPercentages[index] || 0
        : 0;

      return {
        width: `${pct}%`,
        minWidth: "0",
        height: "100%",
        backgroundColor: this.TVZTheme.colors.teal,
        flexShrink: "0",
        transition: "width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      };
    },

    /* ─── Core animation ─── */
    animateTo(index, from, to) {
      const key = `pct_${index}`;

      if (anim.frames[key]) {
        cancelAnimationFrame(anim.frames[key]);
        delete anim.frames[key];
      }
      anim.targets[key] = to;

      if (from === to) {
        this.$set(this.animatedPercentages, index, to);
        return;
      }

      const duration = this.animationDuration;
      const startTime = performance.now();
      const diff = to - from;

      const tick = (now) => {
        // If a newer call superseded this one, bail
        if (anim.targets[key] !== to) return;

        const progress = Math.min((now - startTime) / duration, 1);
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        this.$set(
          this.animatedPercentages,
          index,
          Math.round(from + diff * easeOutQuart)
        );

        if (progress < 1) {
          anim.frames[key] = requestAnimationFrame(tick);
        } else {
          this.$set(this.animatedPercentages, index, to);
          delete anim.frames[key];
        }
      };

      anim.frames[key] = requestAnimationFrame(tick);
    },

    /* ─── Init / animate in ─── */
    initPercentages(animate = false) {
      const rounded = this.roundedPercentages;
      (this.item?.enquete_opcoes || []).forEach((opcao, index) => {
        const target = rounded[index] || 0;
        if (animate) {
          this.$set(this.animatedPercentages, index, 0);
          setTimeout(() => this.animateTo(index, 0, target), 150 + index * 200);
        } else {
          this.$set(this.animatedPercentages, index, target);
        }
      });
    },

    stopAll() {
      Object.keys(anim.frames).forEach((k) => {
        cancelAnimationFrame(anim.frames[k]);
        delete anim.frames[k];
      });
      Object.keys(anim.targets).forEach((k) => delete anim.targets[k]);
    },

    resetPercentages() {
      this.stopAll();
      this.animatedPercentages = [];
    },
  },

  mounted() {
    if (this.item?.flg_exibir_resultado) {
      this.initPercentages(false);
      this.previousResultsVisible = true;
    } else {
      this.resetPercentages();
      this.previousResultsVisible = false;
    }
  },

  beforeDestroy() {
    this.stopAll();
  },

  watch: {
    "item.flg_exibir_resultado": {
      handler(newVal) {
        if (newVal && !this.previousResultsVisible) {
          this.initPercentages(true); // animate in from 0
        } else if (newVal && this.previousResultsVisible) {
          this.initPercentages(false); // refresh without animation
        } else if (!newVal) {
          this.resetPercentages();
        }
        this.previousResultsVisible = !!newVal;
      },
      immediate: true,
    },

    "item.enquete_opcoes": {
      deep: true,
      handler(opcoes) {
        if (!this.item?.flg_exibir_resultado || !this.previousResultsVisible) return;
        const rounded = this.roundedPercentages;
        opcoes.forEach((opcao, index) => {
          const target = rounded[index] || 0;
          if (anim.targets[`pct_${index}`] !== target) {
            this.animateTo(index, this.animatedPercentages[index] || 0, target);
          }
        });
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "Gobold Regular";
  src: url("@/assets/Gobold_Regular.otf") format("opentype");
}

@font-face {
  font-family: "Gobold Thin";
  src: url("@/assets/Gobold_Thin.otf") format("opentype");
}

/* ─────────────────────────────────────────────
   Wrapper — anchored to the bottom of the
   1920×1080 broadcast canvas.
   height: 151px matches the SVG red bar rect.
   overflow: visible so the stepped corner
   polygons can extend above the bar.
───────────────────────────────────────────── */
.enquete-wrapper {
  position: absolute;
  bottom: 20px;
  left: 0;
  width: 100%;
  height: 151px;
  overflow: visible;
  pointer-events: none;
}

/* SVG background — fills the full width, auto height */
.enquete-bg {
  position: relative;
  bottom: 0;
  left: 0;
  width: 90%;
  margin: 0 auto;
  height: 100%;
  display: block;
}

/* Content overlay — absolutely covers the 151px bar.
   Shifted 20px down to align content with SVG geometry,
   with 5px bottom breathing room. */
.enquete-overlay {
  position: relative;
  bottom: 125px;
  left: 0;
  width: 90%;
  margin: 0 auto;
  height: 151px;
  display: flex;
  flex-direction: column;
  padding-bottom: 5px;
}

/* Question strip — fixed height, horizontal padding via inline style */
.enquete-question {
  flex: 0 0 32px;
  display: flex;
  align-items: center;
  padding: 0 56px;
  overflow: hidden;
  position: relative;
  top: 15px;
}

/* Options row — fills remaining height, padding via :style binding */
.enquete-options {
  flex: 1;
  overflow: hidden;
}

/* Inner flex row — fills full height of options row */
.options-inner {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  align-items: center;
}

/* Single option — row: badge + data column */
.enquete-option {
  /* layout via :style binding */
}

/* Data column — stacks label bar above percent bar */
.option-data {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.option-label-bar {
  overflow: hidden;
}

.option-label {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-percent-row {
  /* layout via :style binding */
}

.bar-fill {
  flex-shrink: 0;
}

/* Option entrance transition */
.option-slide-enter-active {
  transition: opacity 0.5s ease, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}
.option-slide-enter {
  opacity: 0;
  transform: translateY(14px);
}
</style>
